/**
 * FALL — the family that carries most of the catalogue.
 *
 * Snow, rain, leaves, petals, hearts, coins, confetti-fall, emoji rain and
 * custom-image rain are ONE integrator with different atlases. That is the whole
 * reason a 108-variant catalogue fits in a few kilobytes.
 *
 * The physics matters commercially, not just aesthetically. A merchant left a
 * 3-star review on a rival saying the snow "is not snowflakes, but circles", and
 * the praise words that recur across 180 positive reviews are "like real" and
 * "fall smoothly and naturally". Four things produce that read, and cheap
 * implementations skip all of them:
 *
 *   1. DEPTH. Particles sit in 1-3 bands. Far particles are smaller, slower,
 *      fainter and blurrier-looking; near ones are larger and faster. A single
 *      depth reads as a screensaver.
 *   2. A SIZE/SPEED DISTRIBUTION, not a constant. Terminal velocity scales with
 *      size, so a big flake genuinely falls faster than a small one.
 *   3. SWAY with a per-particle phase. Real flakes oscillate as they fall and
 *      they do not do it in lockstep. One shared oscillator is instantly readable
 *      as fake.
 *   4. WIND that displaces horizontally without tilting the sprite absurdly.
 */

import { clamp } from '../budget.ts';
import type { Family, Particle, StepContext } from '../types.ts';

/** Depth band tuning: [sizeScale, speedScale, alpha]. Index is the band. */
const BANDS: ReadonlyArray<readonly [number, number, number]> = [
  [0.55, 0.55, 0.45], // far
  [0.8, 0.8, 0.75], // mid
  [1.15, 1.2, 1.0], // near
];

export interface FallOptions {
  /** Bands in use, 1..3 (CampaignDesign.depth). */
  depth: 1 | 2 | 3;
  /** Base sprite long edge in CSS px, before band and design scaling. */
  baseSize: number;
  /** Base fall speed in CSS px/second at band scale 1. */
  baseSpeed: number;
  /** Horizontal sway amplitude in CSS px. 0 for rain, high for leaves. */
  sway: number;
  /** Sway cycles per second. Kept low — this is not a flash. */
  swayHz: number;
  /** Max tumble, radians/second. 0 for rain, high for leaves. */
  spin: number;
  /** Number of atlas cells available to pick from. */
  cellCount: number;
  /** Number of tint rows available. */
  tintCount: number;
  /** Size multiplier from CampaignDesign.size. */
  sizeScale: number;
  /**
   * Maximum drift height in CSS px. 0 or absent turns accumulation off.
   *
   * Only meaningful for snow-like effects. Leaves settling into a perfectly
   * even bank at the bottom of a product page look like a bug, not autumn.
   */
  accumulate?: number;
  /** Drift colour. Falls back to white — the only sensible default for snow. */
  driftColor?: string;
}

/**
 * Width of one accumulation column, in CSS px.
 *
 * 16 is a deliberate middle: at 4 px the drift is a noisy comb and the buffer
 * is four times larger; at 64 px it is visibly a bar chart. At 16 a 1440 px
 * viewport is 90 floats, which costs nothing and reads as a surface.
 */
const COLUMN_PX = 16;

/**
 * Proportional melt per second.
 *
 * ⚠ This is what makes the drift SELF-LIMITING, and it matters more than the
 * hard cap. Deposit is roughly constant (particles per second) while melt is
 * proportional to height, so the drift settles at an equilibrium on its own
 * instead of racing to the ceiling and sitting there. A shopper who leaves a tab
 * open for an hour should not come back to a screen that is half white.
 *
 * Equilibrium height is `landings_per_second x DEPOSIT_PX / MELT_PER_SEC`, per
 * column. With a typical field that is roughly a third of a landing per column
 * per second, so these two numbers are chosen TOGETHER to put a visible drift in
 * reach — see the note on DEPOSIT_K.
 */
const MELT_PER_SEC = 0.03;

/**
 * Drift height contributed by one landing, as a multiple of `size / COLUMN_PX`.
 *
 * ⚠ Not physical, and the first value was too small to work at all.
 *
 * At 0.5 a 14 px snowflake contributes 0.44 px — LESS than the 0.5 px floor
 * below which the melt loop snapped a column to zero. So every landing was
 * erased on the very next frame and the drift could never start. The unit tests
 * passed anyway, because they landed two hundred flakes in a row and only THEN
 * called `overlay`: an ordering the render loop never produces. Found by looking
 * at the actual pixels, which showed particles being consumed at the bottom of
 * the canvas and nothing being drawn there.
 */
const DEPOSIT_K = 2;

/** Below this a column is invisible and only costs arithmetic. */
const MIN_VISIBLE_PX = 0.05;

export function createFallFamily(opts: FallOptions): Family {
  const bands = BANDS.slice(0, opts.depth);

  // ── accumulation state ───────────────────────────────────────────────────
  const maxDrift = opts.accumulate ?? 0;
  /** Per-column drift height in CSS px. Rebuilt on resize. */
  let heights: Float32Array = new Float32Array(0);

  const ensureColumns = (w: number) => {
    const n = Math.max(1, Math.ceil(w / COLUMN_PX));
    if (heights.length === n) return;
    /**
     * ⚠ RESAMPLE, do not reset.
     *
     * A resize is a phone rotating or a merchant dragging a window edge, and
     * throwing the drift away makes the snow they had been watching vanish in
     * one frame. Resampling keeps the shape and simply restretches it.
     */
    const next = new Float32Array(n);
    if (heights.length > 0) {
      for (let i = 0; i < n; i++) {
        next[i] = heights[Math.min(heights.length - 1, Math.floor((i * heights.length) / n))];
      }
    }
    heights = next;
  };

  /** Drift height under a given x, in CSS px. */
  const driftAt = (x: number) => {
    if (heights.length === 0) return 0;
    const i = Math.min(heights.length - 1, Math.max(0, Math.floor(x / COLUMN_PX)));
    return heights[i];
  };

  return {
    id: 'fall',

    spawn(p: Particle, ctx: StepContext, rnd: () => number) {
      const band = (rnd() * bands.length) | 0;
      const [sizeScale, speedScale, alpha] = bands[band];

      p.depth = band;
      p.alpha = alpha;
      p.cell = (rnd() * opts.cellCount) | 0;
      p.tint = (rnd() * opts.tintCount) | 0;

      // A distribution, not a constant. 0.7..1.3 of the band size.
      const jitter = 0.7 + rnd() * 0.6;
      p.size = opts.baseSize * sizeScale * jitter * opts.sizeScale;

      p.x = rnd() * ctx.w;
      // Spread the initial field across the viewport rather than dropping a
      // curtain from y=0 — otherwise every campaign visibly "starts".
      p.y = rnd() * ctx.h - ctx.h * 0.15;

      // Terminal velocity scales with size: bigger falls faster, which is what
      // makes a mixed field read as depth rather than as noise.
      p.vy = opts.baseSpeed * speedScale * (0.75 + jitter * 0.35);
      p.vx = 0;

      p.r = rnd() * Math.PI * 2;
      p.vr = (rnd() - 0.5) * 2 * opts.spin;

      p.age = 0;
      p.phase = rnd() * Math.PI * 2;
    },

    step(p: Particle, ctx: StepContext): boolean {
      p.age += ctx.dt;

      const bandSpeed = BANDS[p.depth][1];

      // Sway: per-particle phase, low frequency. The near band sways more.
      const swayPx = opts.sway * bandSpeed * Math.sin(p.phase + ctx.t * opts.swayHz * Math.PI * 2);

      // Wind displaces near particles more than far ones — parallax.
      const windPx = ctx.wind * 90 * bandSpeed;

      p.vx = swayPx + windPx;
      p.x += p.vx * ctx.dt;
      p.y += p.vy * ctx.speed * ctx.dt;
      p.r += p.vr * ctx.dt;

      // Wrap horizontally so a windy field never thins out on one side.
      const margin = p.size;
      if (p.x < -margin) p.x = ctx.w + margin;
      else if (p.x > ctx.w + margin) p.x = -margin;

      if (maxDrift > 0) {
        ensureColumns(ctx.w);
        // Land ON the drift surface, not on the viewport floor — otherwise the
        // first flake settles and every one after it falls straight through it.
        if (p.y >= ctx.h - driftAt(p.x)) {
          const i = Math.min(heights.length - 1, Math.max(0, Math.floor(p.x / COLUMN_PX)));
          /**
           * Spread each landing over three columns, 1-2-1.
           *
           * Depositing into one column makes a comb of spikes: particles are
           * uniformly distributed in x, so neighbouring columns diverge on
           * random walks and never smooth each other out. The kernel is what
           * turns a histogram into a surface.
           */
          const add = (p.size / COLUMN_PX) * DEPOSIT_K;
          heights[i] = Math.min(maxDrift, heights[i] + add);
          if (i > 0) heights[i - 1] = Math.min(maxDrift, heights[i - 1] + add * 0.5);
          if (i < heights.length - 1)
            heights[i + 1] = Math.min(maxDrift, heights[i + 1] + add * 0.5);
          return false;
        }
      }

      // Recycle once fully below the fold.
      return p.y <= ctx.h + p.size;
    },

    /**
     * The drift.
     *
     * ⚠ `overlay` runs ONCE per layer per frame, before any particle has
     * replaced the transform — so it is in CSS pixels and costs one path fill,
     * not one per column. Melt happens here rather than in `step` for the same
     * reason: `step` runs per particle, and decaying the whole buffer hundreds
     * of times a frame would make the melt rate depend on the particle count.
     */
    overlay:
      maxDrift > 0
        ? (g: CanvasRenderingContext2D, ctx: StepContext) => {
            ensureColumns(ctx.w);
            const decay = Math.pow(1 - MELT_PER_SEC, ctx.dt);
            let any = false;
            for (let i = 0; i < heights.length; i++) {
              heights[i] *= decay;
              // Below a pixel it is invisible and only costs arithmetic.
              if (heights[i] < MIN_VISIBLE_PX) heights[i] = 0;
              else any = true;
            }
            if (!any) return;

            g.beginPath();
            g.moveTo(0, ctx.h);
            for (let i = 0; i < heights.length; i++) {
              // Midpoint quadratics: the control point is the column's own
              // height and the curve passes between neighbours, which is what
              // makes a coarse buffer read as a smooth bank of snow.
              const x = i * COLUMN_PX + COLUMN_PX / 2;
              const y = ctx.h - heights[i];
              if (i === 0) g.lineTo(x, y);
              else {
                const px = (i - 1) * COLUMN_PX + COLUMN_PX / 2;
                const py = ctx.h - heights[i - 1];
                g.quadraticCurveTo(px, py, (px + x) / 2, (py + y) / 2);
              }
            }
            g.lineTo(ctx.w, ctx.h - heights[heights.length - 1]);
            g.lineTo(ctx.w, ctx.h);
            g.closePath();
            g.fillStyle = opts.driftColor ?? '#FFFFFF';
            g.fill();
          }
        : undefined,
  };
}

/** Presets, so a catalogue entry is a few numbers rather than a new file. */
export const FALL_PRESETS = {
  snow: { baseSize: 14, baseSpeed: 46, sway: 18, swayHz: 0.22, spin: 0.5 },
  rain: { baseSize: 18, baseSpeed: 420, sway: 0, swayHz: 0, spin: 0 },
  leaves: { baseSize: 26, baseSpeed: 58, sway: 44, swayHz: 0.3, spin: 2.2 },
  petals: { baseSize: 18, baseSpeed: 42, sway: 38, swayHz: 0.26, spin: 1.6 },
  hearts: { baseSize: 20, baseSpeed: 52, sway: 24, swayHz: 0.24, spin: 0.8 },
  confetti: { baseSize: 12, baseSpeed: 90, sway: 30, swayHz: 0.45, spin: 4.5 },
  coins: { baseSize: 20, baseSpeed: 120, sway: 12, swayHz: 0.2, spin: 3.0 },
  emoji: { baseSize: 24, baseSpeed: 60, sway: 22, swayHz: 0.25, spin: 0.6 },
} as const satisfies Record<
  string,
  Pick<FallOptions, 'baseSize' | 'baseSpeed' | 'sway' | 'swayHz' | 'spin'>
>;

export type FallPresetId = keyof typeof FALL_PRESETS;

export function fallOptions(
  preset: FallPresetId,
  o: Pick<FallOptions, 'depth' | 'cellCount' | 'tintCount' | 'sizeScale'> &
    Partial<Pick<FallOptions, 'accumulate' | 'driftColor'>>
): FallOptions {
  const base = FALL_PRESETS[preset];
  return {
    ...base,
    ...o,
    // swayHz is a motion frequency; keep it far below the WCAG 2.3.1 flash bar.
    swayHz: clamp(base.swayHz, 0, 1),
  };
}
